import { useCallback, useEffect, useRef } from 'react';
import { SpinnerCircular } from 'spinners-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { consumeResumeToken } from '../../services/reminder';
import { setAccountId, setPartnerInfo } from '../../utils/storage';
import { handleApiError } from '../../utils/apiError';
import { markRegistrationStarted } from '../../analytics/registrationMeta';

import '../../hashVerify/hashVerify.css';

const ALLOWED_NEXT = new Set([
    '/email',
    '/phone',
    '/verification-options',
    '/conditions',
    '/info',
    '/sumsub',
    '/imid-verification',
]);

function sanitizeNext(candidate: string | null | undefined, fallback: string): string {
    if (!candidate) {
        return fallback;
    }
    const path = candidate.startsWith('/') ? candidate : `/${candidate}`;
    // Reject open redirects
    if (path.includes('://') || path.includes('//')) {
        return fallback;
    }
    return ALLOWED_NEXT.has(path) ? path : fallback;
}

const Resume = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const started = useRef(false);

    const run = useCallback(() => {
        const token = params.get('token');
        const nextParam = params.get('next');

        if (!token) {
            navigate('/error', { replace: true });
            return;
        }

        consumeResumeToken(token)
            .then((result) => {
                setAccountId(result.accountId);
                setPartnerInfo({
                    email: result.email ?? undefined,
                    phone: result.phone ?? undefined,
                    docNumber: result.docNumber ?? undefined,
                    docType: result.docType ?? undefined,
                });
                markRegistrationStarted();
                const target = sanitizeNext(nextParam || result.nextPath, result.nextPath || '/email');
                navigate(target, { replace: true });
            })
            .catch((e: unknown) => handleApiError(e, navigate));
    }, [params, navigate]);

    useEffect(() => {
        if (started.current) {
            return;
        }
        started.current = true;
        run();
    }, [run]);

    return (
        <div className="verify-wrapper">
            <SpinnerCircular size={150} thickness={200} />
        </div>
    );
};

export default Resume;
